import { ImpactSectorType, NumberVerticalType, OrganisationEntityType } from "graphql/generated/types";
import { ISelectOption } from "lib/form/Select";
import { CloseConversationSetting } from "pages/NumberDetail/pages/Inbox/utils";
import { IOrderOption, ReplyType } from "types";
import { ORDERING } from "utils/constants";
import { dayjs } from "utils/date";
import i18n from "utils/i18n";

export const toastOptions = {
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: true,
  progress: undefined,
  containerId: "defaultToast",
};

export const switchDefaultOptions = [
  {
    display: i18n.t("common:global.yes"),
    value: true,
  },
  {
    display: i18n.t("common:global.no"),
    value: false,
  },
];

export const noSelectValue = (labelKey = "label", valueKey = "value") => ({
  [labelKey]: i18n.t("common:form.no-value"),
  [valueKey]: null,
});

const orderingOptions: IOrderOption[] = [
  { value: ORDERING.LATEST_FIRST, display: i18n.t("ordering.newest") },
  { value: ORDERING.OLDEST_FIRST, display: i18n.t("ordering.oldest") },
  { value: ORDERING.RECENTLY_UPDATED, display: i18n.t("ordering.recently-updated") },
  { value: ORDERING.ALPHABET_ASC, display: i18n.t("ordering.name-asc") },
  { value: ORDERING.ALPHABET_DESC, display: i18n.t("ordering.name-desc") },
];

export const getOrderingOptions = (keys: string[], titleKey?: string) =>
  (keys.map((key) => orderingOptions.find(({ value }) => value === key)) as IOrderOption[]).map(
    ({ value, display }) => ({
      display,
      value: titleKey && value.includes("title") ? value.replace("title", titleKey) : value,
    })
  );

export const getContentOrderingOptions = (titleKey?: string) =>
  getOrderingOptions(
    [ORDERING.LATEST_FIRST, ORDERING.OLDEST_FIRST, ORDERING.ALPHABET_ASC, ORDERING.ALPHABET_DESC],
    titleKey
  );

export const playbooksOrderingOptions = getOrderingOptions([
  ORDERING.LATEST_FIRST,
  ORDERING.OLDEST_FIRST,
  ORDERING.RECENTLY_UPDATED,
]);

export const automationOrderingOptions = getOrderingOptions(
  [
    ORDERING.LATEST_FIRST,
    ORDERING.OLDEST_FIRST,
    ORDERING.RECENTLY_UPDATED,
    ORDERING.ALPHABET_ASC,
    ORDERING.ALPHABET_DESC,
  ],
  "name"
);

export const remindersOrderingOptions = getOrderingOptions(
  [ORDERING.LATEST_FIRST, ORDERING.OLDEST_FIRST, ORDERING.ALPHABET_ASC, ORDERING.ALPHABET_DESC],
  "name"
);

const getLocalizedTimerStartOption = (hour: number, minume: number) => {
  const date = dayjs().hour(hour).minute(minume).second(0);

  return {
    label: i18n.t("automation.timer.at", { time: date.format("LT").toLowerCase() }),
    value: { trigger_at: date.format("HH:mm:ss") },
  };
};

const getLocalizedTimerStartInOption = (hours: number, minutes: number) => ({
  label: minutes
    ? i18n.t("automation.timer.in-hours-min", { count: hours, minutes })
    : i18n.t("automation.timer.in-hours", { count: hours }),
  value: {
    duration: `PT${hours}H${minutes ? `${minutes}M` : ""}`,
  },
});

export const timerOptions = [
  { label: i18n.t("automation.timer.at-midnight"), value: { trigger_at: "00:00:00" } },
  { label: i18n.t("automation.timer.at-half-past-midnight"), value: { trigger_at: "00:30:00" } },
  ...(
    [
      [1, 0],
      [1, 30],
      [2, 0],
      [2, 30],
      [3, 0],
      [3, 30],
      [4, 0],
      [4, 30],
      [5, 0],
      [5, 30],
      [6, 0],
      [6, 30],
      [7, 0],
      [7, 30],
      [8, 0],
      [8, 30],
      [9, 0],
      [9, 30],
      [10, 0],
      [10, 30],
      [11, 0],
      [11, 30],
    ] as [number, number][]
  ).map((time) => getLocalizedTimerStartOption(...time)),
  { label: "at noon local time", value: { trigger_at: "12:00:00" } },
  ...(
    [
      [12, 30],
      [13, 0],
      [13, 30],
      [14, 0],
      [14, 30],
      [15, 0],
      [15, 30],
      [16, 0],
      [16, 30],
      [17, 0],
      [17, 30],
      [18, 0],
      [18, 30],
      [19, 0],
      [19, 30],
      [20, 0],
      [20, 30],
      [21, 0],
      [21, 30],
      [22, 0],
      [22, 30],
      [23, 0],
      [23, 30],
    ] as [number, number][]
  ).map((time) => getLocalizedTimerStartOption(...time)),
  { label: i18n.t("automation.timer.in-min", { minutes: 30 }), value: { duration: "PT30M" } },
  { label: i18n.t("automation.timer.in-hours", { count: 1 }), value: { duration: "PT1H" } },
  ...(
    [
      [1, 30],
      [2, 0],
      [2, 30],
      [3, 0],
      [3, 30],
      [4, 0],
      [4, 30],
      [5, 0],
      [5, 30],
      [6, 0],
      [6, 30],
      [7, 0],
      [7, 30],
      [8, 0],
      [8, 30],
      [9, 0],
      [9, 30],
      [10, 0],
      [10, 30],
      [11, 0],
      [11, 30],
      [12, 0],
      [12, 30],
      [13, 0],
      [13, 30],
      [14, 0],
      [14, 30],
      [15, 0],
      [15, 30],
      [16, 0],
      [16, 30],
      [17, 0],
      [17, 30],
      [18, 0],
      [18, 30],
      [19, 0],
      [19, 30],
      [20, 0],
      [20, 30],
      [21, 0],
      [21, 30],
      [22, 0],
      [22, 30],
      [23, 0],
      [23, 30],
    ] as [number, number][]
  ).map((time) => getLocalizedTimerStartInOption(...time)),
];

export const otherLicenseOption = { value: "Other", label: i18n.t("global.other") };
export const licenseOptions: { value: string; label: string; href?: string }[] = [
  {
    value: "Creative Commons Sharealike 4.0",
    label: `Creative Commons Sharealike 4.0 (${i18n.t("global.recommended")})`,
    href: "https://creativecommons.org/licenses/by-sa/4.0/",
  },
  { value: "Apache License 2.0", label: "Apache License 2.0", href: "https://www.apache.org/licenses/LICENSE-2.0" },
  {
    value: "GNU General Public License v3.0",
    label: "GNU General Public License v3.0",
    href: "https://www.gnu.org/licenses/gpl-3.0.html",
  },
  { value: "MIT License", label: "MIT License", href: "https://opensource.org/licenses/MIT" },
  otherLicenseOption,
];

export const impactAreas = [
  {
    name: i18n.t("options.impact-area.HEALTH"),
    value: ImpactSectorType.HEALTH,
  },
  {
    name: i18n.t("options.impact-area.EDUCATION"),
    value: ImpactSectorType.EDUCATION,
  },
  {
    name: i18n.t("options.impact-area.FINANCIAL_INCLUSION"),
    value: ImpactSectorType.FINANCIAL_INCLUSION,
  },
  {
    name: i18n.t("options.impact-area.MEDIA"),
    value: ImpactSectorType.MEDIA,
  },
  {
    name: i18n.t("options.impact-area.SUSTAINABILITY"),
    value: ImpactSectorType.SUSTAINABILITY,
  },
  {
    name: i18n.t("options.impact-area.AGRICULTURE"),
    value: ImpactSectorType.AGRICULTURE,
  },
  {
    name: i18n.t("options.impact-area.CIVIC_ENGAGEMENT"),
    value: ImpactSectorType.CIVIC_ENGAGEMENT,
  },
  {
    name: i18n.t("options.impact-area.HUMANITARIAN"),
    value: ImpactSectorType.HUMANITARIAN,
  },
  {
    name: i18n.t("options.impact-area.COVID_19"),
    value: ImpactSectorType.COVID_19,
  },
  {
    name: i18n.t("options.impact-area.OTHER"),
    value: ImpactSectorType.OTHER,
  },
];

export const organisationTypes = [
  {
    name: i18n.t("options.organisation-type.NON_PROFIT_NGO"),
    value: OrganisationEntityType.NON_PROFIT_NGO,
  },
  {
    name: i18n.t("options.organisation-type.FOR_PROFIT_SOCIAL_ENTERPRISE"),
    value: OrganisationEntityType.FOR_PROFIT_SOCIAL_ENTERPRISE,
  },
  {
    name: i18n.t("options.organisation-type.GOVERNMENT_GOVERNMENT_AGENCY"),
    value: OrganisationEntityType.GOVERNMENT_GOVERNMENT_AGENCY,
  },
  {
    name: i18n.t("options.organisation-type.INGO"),
    value: OrganisationEntityType.INGO,
  },
  {
    name: i18n.t("options.organisation-type.UNITED_NATIONS"),
    value: OrganisationEntityType.UNITED_NATIONS,
  },
];

export const dontSaveOption: ISelectOption = {
  label: i18n.t("global.dont-save"),
  value: undefined,
};

export const convertToOptions = (data: any[] = [], labelKey: string, valueKey: string): ISelectOption[] =>
  data.reduce((options, item) => {
    const label = item[labelKey];

    if (label) {
      options.push({
        label,
        value: item[valueKey],
      });
    }

    return options;
  }, []);

// vertical list https://developers.facebook.com/docs/whatsapp/api/settings/business-profile#set
export const verticalOptions = [
  {
    label: i18n.t("options.number-vertical-type.AUTOMOTIVE"),
    value: NumberVerticalType.AUTOMOTIVE,
  },
  {
    label: i18n.t("options.number-vertical-type.BEAUTY_SPA_AND_SALON"),
    value: NumberVerticalType.BEAUTY_SPA_AND_SALON,
  },
  {
    label: i18n.t("options.number-vertical-type.CLOTHING_AND_APPAREL"),
    value: NumberVerticalType.CLOTHING_AND_APPAREL,
  },
  {
    label: i18n.t("options.number-vertical-type.EDUCATION"),
    value: NumberVerticalType.EDUCATION,
  },
  {
    label: i18n.t("options.number-vertical-type.ENTERTAINMENT"),
    value: NumberVerticalType.ENTERTAINMENT,
  },
  {
    label: i18n.t("options.number-vertical-type.EVENT_PLANNING_AND_SERVICE"),
    value: NumberVerticalType.EVENT_PLANNING_AND_SERVICE,
  },
  {
    label: i18n.t("options.number-vertical-type.FINANCE_AND_BANKING"),
    value: NumberVerticalType.FINANCE_AND_BANKING,
  },
  {
    label: i18n.t("options.number-vertical-type.FOOD_AND_GROCERY"),
    value: NumberVerticalType.FOOD_AND_GROCERY,
  },
  {
    label: i18n.t("options.number-vertical-type.PUBLIC_SERVICE"),
    value: NumberVerticalType.PUBLIC_SERVICE,
  },
  {
    label: i18n.t("options.number-vertical-type.HOTEL_AND_LODGING"),
    value: NumberVerticalType.HOTEL_AND_LODGING,
  },
  {
    label: i18n.t("options.number-vertical-type.MEDICAL_AND_HEALTH"),
    value: NumberVerticalType.MEDICAL_AND_HEALTH,
  },
  {
    label: i18n.t("options.number-vertical-type.NON_PROFIT"),
    value: NumberVerticalType.NON_PROFIT,
  },
  {
    label: i18n.t("options.number-vertical-type.PROFESSIONAL_SERVICES"),
    value: NumberVerticalType.PROFESSIONAL_SERVICES,
  },
  {
    label: i18n.t("options.number-vertical-type.SHOPPING_AND_RETAIL"),
    value: NumberVerticalType.SHOPPING_AND_RETAIL,
  },
  {
    label: i18n.t("options.number-vertical-type.TRAVEL_AND_TRANSPORTATION"),
    value: NumberVerticalType.TRAVEL_AND_TRANSPORTATION,
  },
  {
    label: i18n.t("options.number-vertical-type.RESTAURANT"),
    value: NumberVerticalType.RESTAURANT,
  },
  {
    label: i18n.t("options.number-vertical-type.OTHER"),
    value: NumberVerticalType.OTHER,
  },
];

export const searchQuickPickButtons = [
  { label: "btn.24-hours", value: "24-hours" },
  { label: "btn.3-days", value: "3-days" },
  { label: "btn.7-days", value: "7-days" },
  { label: "btn.30-days", value: "30-days" },
];

export const closeConversationSettingOptions = [
  {
    label: i18n.t(`conversation.close-settings.${CloseConversationSetting.KEEP_ASSIGNMENT}`),
    value: CloseConversationSetting.KEEP_ASSIGNMENT,
  },
  {
    label: i18n.t(`conversation.close-settings.${CloseConversationSetting.UNASSIGN}`),
    value: CloseConversationSetting.UNASSIGN,
  },
];

export const replyTypeOptions = [
  {
    label: i18n.t(`btn.${ReplyType.DEFAULT}`),
    value: ReplyType.DEFAULT,
  },
  {
    label: i18n.t(`btn.${ReplyType.HANDOVER}`),
    value: ReplyType.HANDOVER,
  },
];
